import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage } from '../../../../../util/reactIntl';
import { formatMoney } from '../../../../../util/currency';
import { useIntl } from '../../../../../util/reactIntl';
import { createSlug } from '../../../../../util/urlHelpers';
import { canCheckoutSellerCart, goToCartCheckout } from '../../../../../util/cartCheckout';
import { useRouteConfiguration } from '../../../../../context/routeConfigurationContext';
import { useConfiguration } from '../../../../../context/configurationContext';
import { getCartTotalItemCount, getSellerIds } from '../../../../../util/cartHelpers';
import { getCartItemCount, getSellerCartListingIds } from '../../../../../config/configCart';
import { ensureUser, userDisplayNameAsString } from '../../../../../util/data';
import {
  ExtraIcons,
  IconDelete,
  NamedLink,
  ResponsiveImage,
  AvatarSmall,
} from '../../../../../components';
import { toggleCart } from '../../../../CartPage/CartPage.duck';

import css from './CartMenu.module.css';

// Keep the panel open briefly when the pointer travels from the icon to the
// panel, otherwise the gap between them closes it mid-move.
const CLOSE_DELAY_MS = 180;

// The bin sits beside the link rather than inside it, so tapping it on a touch
// screen removes the book instead of opening its page.
const CartMenuItem = ({ listing, count, intl, onRemove, removeDisabled }) => {
  const { title, price } = listing.attributes;
  const firstImage = listing.images?.length > 0 ? listing.images[0] : null;
  const stock = listing.currentStock?.attributes?.quantity;
  const isShort = typeof stock === 'number' && stock < count;
  const removeLabel = intl.formatMessage({ id: 'CartCard.remove' });

  return (
    <div className={css.itemRow}>
      <NamedLink
        className={css.item}
        name="ListingPage"
        params={{ id: listing.id.uuid, slug: createSlug(title) }}
      >
        <div className={css.itemImageWrapper}>
          <ResponsiveImage
            rootClassName={css.itemImage}
            alt={title}
            image={firstImage}
            variants={['scaled-small', 'square-small', 'square-small2x']}
          />
        </div>
        <div className={css.itemInfo}>
          <span className={css.itemTitle}>{title}</span>
          <span className={css.itemMeta}>
            {count} &times; {price ? formatMoney(intl, price) : null}
          </span>
          {isShort ? (
            <span className={css.itemOutOfStock}>
              {stock === 0 ? (
                <FormattedMessage id="CartCard.outOfStock" />
              ) : (
                <FormattedMessage id="CartCard.onlyLeft" values={{ count: stock }} />
              )}
            </span>
          ) : null}
        </div>
      </NamedLink>
      <button
        type="button"
        className={css.removeButton}
        onClick={onRemove}
        disabled={removeDisabled}
        title={removeLabel}
        aria-label={removeLabel}
      >
        <IconDelete className={css.removeIcon} />
      </button>
    </div>
  );
};

/**
 * Topbar cart icon with a drawer previewing the cart.
 *
 * The drawer opens on hover, and is also pushed open from the cart duck when an
 * item is added, so the buyer gets immediate feedback wherever they added from.
 *
 * @param {Object} props
 * @param {Object} props.cart - currentUser.attributes.profile.privateData.cart
 * @param {Array} props.cartListings - Cart listing entities from marketplaceData
 * @param {boolean} props.isDrawerOpen - Pushed open after an add-to-cart
 * @param {Function} props.onSetDrawerOpen
 * @param {Function} props.onFetchCartListings
 * @param {Function} props.callSetInitialValues
 * @param {Function} props.onInitializeCardPaymentData
 */
const CartMenu = props => {
  const {
    cart = {},
    cartListings = [],
    isDrawerOpen = false,
    // Both run from effects on mount, so default them like the data props: a
    // topbar rendered without cart wiring (a test, a page without the cart
    // duck) then shows an empty cart instead of crashing.
    onSetDrawerOpen = () => {},
    onFetchCartListings = () => {},
    callSetInitialValues,
    onInitializeCardPaymentData,
  } = props;

  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const dispatch = useDispatch();
  const toggleCartInProgress = useSelector(state => !!state.CartPage?.toggleCartInProgress);

  const [isHovered, setIsHovered] = useState(false);
  const hasFetched = useRef(false);
  const closeTimer = useRef(null);

  const cartItemCount = getCartTotalItemCount(cart);
  const hasItems = cartItemCount > 0;
  const isOpen = isHovered || isDrawerOpen;

  useEffect(() => {
    return () => clearTimeout(closeTimer.current);
  }, []);

  // The count gives a little pop when it changes while the page is open (e.g.
  // adding a book from a listing card), but not when a page first loads.
  const previousCount = useRef(cartItemCount);
  const [popKey, setPopKey] = useState(0);
  useEffect(() => {
    if (cartItemCount !== previousCount.current) {
      previousCount.current = cartItemCount;
      setPopKey(k => k + 1);
    }
  }, [cartItemCount]);

  // NamedLink does not forward onClick, so dismiss the drawer on navigation
  // instead. This covers the item links, "View cart" and checkout alike, and a
  // jump to one seller's basket while already on the cart page.
  useEffect(() => {
    clearTimeout(closeTimer.current);
    setIsHovered(false);
    onSetDrawerOpen(false);
  }, [location.pathname, location.search]);

  const fetchOnce = () => {
    if (!hasFetched.current && hasItems) {
      hasFetched.current = true;
      // The config carries the listing image shape, so the fetch also loads the
      // picture size checkout shows; without it a drawer checkout has no image.
      onFetchCartListings(config);
    }
  };

  const open = () => {
    clearTimeout(closeTimer.current);
    setIsHovered(true);
    fetchOnce();
  };

  // Hovering away only cancels the hover; a drawer opened by add-to-cart stays
  // until it is explicitly dismissed.
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setIsHovered(false), CLOSE_DELAY_MS);
  };

  const closeAll = () => {
    clearTimeout(closeTimer.current);
    setIsHovered(false);
    onSetDrawerOpen(false);
  };

  // A click or tap anywhere outside the icon and drawer closes it, as does
  // Escape, so the × is not the only way out of a drawer opened by an add.
  const rootRef = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handlePointerDown = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        closeAll();
      }
    };
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        closeAll();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleCheckout = authorId => {
    goToCartCheckout({
      authorId,
      sellerCart: cart[authorId],
      listings: cartListings,
      routeConfiguration,
      history,
      callSetInitialValues,
      onInitializeCardPaymentData,
    });
  };

  // Group the cart by seller, resolving each listing id against the fetched
  // entities. Sellers whose listings are not loaded yet are skipped.
  const sellerGroups = getSellerIds(cart)
    .map(authorId => {
      const sellerCart = cart[authorId];
      const entries = getSellerCartListingIds(sellerCart)
        .map(listingId => {
          const listing = cartListings.find(l => l.id.uuid === listingId);
          return listing ? { listing, count: sellerCart[listingId]?.count || 0 } : null;
        })
        .filter(Boolean);

      if (entries.length === 0) {
        return null;
      }
      const author = ensureUser(entries[0].listing.author);
      return {
        authorId,
        author,
        authorName: userDisplayNameAsString(author, ''),
        entries,
        itemCount: getCartItemCount(sellerCart),
        // Same rule as the cart page; a basket that fails it goes to the cart
        // to be fixed rather than to a checkout that cannot price it.
        // A saved book that did not load has been closed or deleted (or is
        // still loading); either way the cart page is where it gets sorted.
        canCheckout:
          entries.length === getSellerCartListingIds(sellerCart).length &&
          canCheckoutSellerCart(
            sellerCart,
            entries.map(e => e.listing)
          ),
        needsDeliveryMethod: !sellerCart.deliveryMethod,
      };
    })
    .filter(Boolean);

  const isLoading = hasItems && sellerGroups.length === 0;

  return (
    <div ref={rootRef} className={css.root} onMouseEnter={open} onMouseLeave={scheduleClose}>
      <NamedLink className={css.cartLink} name="CartPage">
        <span className={css.cartLinkLabel}>
          <ExtraIcons className={css.cartIcon} icon="cart" />
          {hasItems ? (
            <span
              key={popKey}
              className={classNames(css.cartBadge, { [css.cartBadgePop]: popKey > 0 })}
            >
              {cartItemCount}
            </span>
          ) : null}
        </span>
      </NamedLink>

      <div className={classNames(css.panel, { [css.panelOpen]: isOpen })}>
        <div className={css.panelHeader}>
          <span className={css.panelTitle}>
            <FormattedMessage id="CartMenu.title" values={{ count: cartItemCount }} />
          </span>
          <button
            type="button"
            className={css.closeButton}
            onClick={closeAll}
            aria-label={intl.formatMessage({ id: 'CartMenu.close' })}
          >
            &times;
          </button>
        </div>

        {!hasItems ? (
          <p className={css.empty}>
            <FormattedMessage id="CartMenu.empty" />
          </p>
        ) : isLoading ? (
          <p className={css.empty}>
            <FormattedMessage id="CartMenu.loading" />
          </p>
        ) : (
          <div className={css.scrollArea}>
            {sellerGroups.map(group => (
              <div key={group.authorId} className={css.sellerGroup}>
                <div className={css.sellerHeader}>
                  <AvatarSmall user={group.author} className={css.sellerAvatar} />
                  <span className={css.sellerName}>{group.authorName}</span>
                </div>
                <div className={css.items}>
                  {group.entries.map(({ listing, count }) => (
                    <CartMenuItem
                      key={listing.id.uuid}
                      listing={listing}
                      count={count}
                      intl={intl}
                      onRemove={() => dispatch(toggleCart(listing.id.uuid, group.authorId, -count))}
                      removeDisabled={toggleCartInProgress}
                    />
                  ))}
                </div>
                {group.canCheckout ? (
                  <button
                    type="button"
                    className={css.checkoutButton}
                    onClick={() => handleCheckout(group.authorId)}
                  >
                    <FormattedMessage id="CartMenu.checkout" values={{ count: group.itemCount }} />
                  </button>
                ) : (
                  <NamedLink
                    className={classNames(css.checkoutButton, css.reviewInCartButton)}
                    name="CartPage"
                    to={{ search: `?seller=${group.authorId}` }}
                  >
                    <FormattedMessage
                      id="CartMenu.reviewInCart"
                      values={{ reason: group.needsDeliveryMethod ? 'method' : 'stock' }}
                    />
                  </NamedLink>
                )}
              </div>
            ))}
          </div>
        )}

        <NamedLink className={css.viewCartButton} name="CartPage">
          <FormattedMessage id="CartMenu.viewCart" />
        </NamedLink>
      </div>
    </div>
  );
};

export default CartMenu;
